import { Avatar, Box, Group, Skeleton, Text, Tooltip } from '@mantine/core';
import {
  getListingAddress,
  getListingName,
  whereListings,
  whereSettingAcrossAutomations,
} from '@vrplatform/ui-common';
import { type Maybe, day, sortBy } from '@vrplatform/utils';
import { useMemo } from 'react';
import { type ApiClient, type ResponseData, useApiQuery } from '../api';
import type { Query } from '../graphql';
import { useMe, useTeam } from '../hooks/team';
import { useAccountingConnectionQuery } from './useAccountingConnectionQuery';
import { useAutomationsQuery } from './useAutomationsQuery';
import { useFinalyticConnectionQuery } from './useFinalyticConnectionQuery';

export type IssueFilter = {
  search: Maybe<string>;
  level?: Maybe<'error' | 'warning'>;
};

export type IssueRow = {
  id: string;
  date: string | null;
  type:
    | 'connection-error'
    | 'connection-version-outdated'
    | 'listing-class-missing'
    | 'listing-owner-missing'
    | 'payment-duplicate';
  table:
    | 'connection'
    | 'owner-statement'
    | 'listing'
    | 'account'
    | 'payment'
    | 'reservation';
  level: 'error' | 'warning';
  message: string;
  item: React.ReactNode;
  itemName: string;
};

function useIssuesListingClassMappingInfo() {
  const { finalyticConnectionId, loading: loadingFinalyticConnection } =
    useFinalyticConnectionQuery();
  const { accounting, loading } = useAccountingConnectionQuery();
  const { data: automations = [], isInitialLoading } = useAutomationsQuery();

  const accountingConnectionId = accounting?.id;

  const billAutomation = useMemo(() => {
    return automations
      .filter((automation) =>
        automation.template.uniqueRef
          ?.toLowerCase()
          .endsWith('_ownerstatementbills')
      )
      .find((automation) =>
        Object.values(automation.connections).includes(accounting?.id || '')
      );
  }, [automations, accounting?.id]);

  const rightType =
    accounting?.appId === 'xero'
      ? 'xero.trackingCategory'
      : `${accounting?.appId}.class`;
  const leftType = 'finalytic.listing';
  const key = 'class';

  return useMemo(
    () => ({
      loading: loading || isInitialLoading || loadingFinalyticConnection,
      mapping: {
        rightType,
        leftType,
        key,
      },
      automation: {
        automation: billAutomation,
        finalyticConnectionId,
        accountingConnectionId,
      },
      accounting,
    }),
    [
      accounting,
      accountingConnectionId,
      billAutomation,
      finalyticConnectionId,
      isInitialLoading,
      loading,
      loadingFinalyticConnection,
      rightType,
    ]
  );
}

export function useLegacyIssuesQuery() {
  const [{ id: teamId }] = useTeam();
  const { id: meId } = useMe();
  const listingClassMapping = useIssuesListingClassMappingInfo();

  return useApiQuery(
    async (api, signal, args, $db) => {
      return await $db(
        async (q) =>
          await getLegacyIssues(
            q,
            {
              teamId: args.teamId,
              GL: args.GL,
              onlyAggregate: args.onlyAggregate,
              search: args.search,
              level: args.level,
              listingClassMapping: args.listingClassMapping,
              api,
            },
            { limit: 2, offset: 0 }
          ),
        {
          fetchOptions: {
            signal,
          },
        }
      );
    },
    {
      queryGroup: 'reports',
      uniqueQueryKey: 'issues-query',
      variables: {
        teamId,
        meId,
        GL: false,
        onlyAggregate: false,
        search: undefined,
        level: 'error' as const,
        listingClassMapping,
      },
      queryKeys: ['issueMessageOverwrites'],
      hideNotification: true,
      skip: !teamId || !meId || listingClassMapping.loading,
    }
  );
}

export async function getLegacyIssues(
  q: Query,
  args: {
    api: ApiClient;
    teamId: string;
    onlyAggregate: boolean;
    GL: boolean;
    listingClassMapping:
      | ReturnType<typeof useIssuesListingClassMappingInfo>
      | undefined;
  } & IssueFilter,
  opts: {
    limit: number;
    offset: number;
  }
): Promise<{
  aggregate: number;
  list: IssueRow[];
}> {
  const limit = opts.limit;
  const offset = opts.offset;

  type IssueCode =
    ResponseData<'get:/teams/{id}/issues'>['issues'][number]['code'];

  const issues =
    args.level === 'warning' || offset !== 0
      ? []
      : await args.api
          .GET('/teams/{id}/issues', {
            params: {
              path: {
                id: args.teamId,
              },
              query: {
                codes: (
                  [
                    'brokenConnections',
                    'outdatedConnections',
                    'duplicatedPayments',
                  ] satisfies IssueCode[]
                ).join(','),
              },
            },
          })
          .then((result) => {
            if (result.error) throw result.error;

            return result.data.issues;
          });

  const outdatedConnectionIssue = issues.find(
    (issue) => issue.code === 'outdatedConnections'
  )?.context;

  const outdatedConnections =
    args.level === 'warning' || offset !== 0
      ? []
      : ((Array.isArray(outdatedConnectionIssue?.connections)
          ? outdatedConnectionIssue.connections
          : []
        ).map<IssueRow>((connection) => {
          return {
            id: connection.id,
            type: 'connection-version-outdated',
            date: day().yyyymmdd(),
            message: 'Please reconnect this connection to update the version.',
            itemName: connection.name || 'Missing connection name',
            item: (
              <IssueConnectionNameCell
                icon={null}
                appId={connection.appId}
                name={connection.name || 'Missing connection name'}
              />
            ),
            level: 'error',
            table: 'connection',
          };
        }) ?? []);

  const duplicatedPaymentsIssue = issues.find(
    (issue) => issue.code === 'duplicatedPayments'
  )?.context;

  const duplicatedPayments =
    args.level === 'warning' || offset !== 0 || args.GL
      ? []
      : (duplicatedPaymentsIssue?.payments.map<IssueRow>((payment) => {
          return {
            id: payment.uniqueRef,
            type: 'payment-duplicate',
            date:
              payment.date && day(payment.date).isValid()
                ? day(payment.date).yyyymmdd()
                : null,
            message: `We found the same payment synced from ${payment.connectionNames?.length ?? 0} different connections.`,
            itemName: payment.uniqueRef || 'Missing payment reference',
            item: (
              <Box
                component="span"
                mr={5}
                sx={{
                  border: '1px solid #EEEFF1',
                  borderRadius: 5,
                  padding: '2px 7px',
                  backgroundColor: '#fff',
                  fontSize: 13,
                  lineHeight: '1rem',
                }}
              >
                {payment.uniqueRef}
              </Box>
            ),
            level: 'error',
            table: 'payment',
          };
        }) ?? []);

  const queriedConnections =
    args.level === 'warning'
      ? {
          total: 0,
          connections: [] as IssueRow[],
        }
      : await args.api
          .GET('/connections', {
            params: {
              query: {
                status: 'active',
                isErrorState: true,
              },
            },
          })
          .then((response) => {
            if (response.error) throw response.error;

            return {
              total: response.data.pagination.total,
              connections: response.data.data.map<IssueRow>((connection) => {
                const lastFetch = connection.currentSync;

                return {
                  id: connection.id,
                  type: 'connection-error',
                  table: 'connection',
                  level: 'error',
                  message: lastFetch?.message || 'Missing error',
                  date: lastFetch?.createdAt || day().yyyymmdd(),
                  itemName: connection.name!,
                  item: (
                    <IssueConnectionNameCell
                      icon={connection.app.icon}
                      name={connection.name}
                    />
                  ),
                };
              }),
            };
          });

  const failedConnections = queriedConnections.connections;
  const failedConnectionAggregate = queriedConnections.total;

  const whereMissingOwner = {
    ...whereListings({
      tenantId: args.teamId,
      dashboard: 'propertyManager',
      search: args.search,
      status: 'active',
      ownerPortalContactId: null,
      GL: false,
    }),
    _not: {
      ownerships: {},
    },
  };

  const missingOwnerAggregate =
    args.level === 'error' || args.GL
      ? 0
      : q
          .listingAggregate({
            where: whereMissingOwner,
          })
          .aggregate?.count() || 0;

  const missingOwners =
    args.onlyAggregate || args.level === 'error' || args.GL
      ? []
      : q
          .listings({
            where: whereMissingOwner,
            limit,
            offset,
            order_by: [
              {
                createdAt: 'desc',
              },
            ],
          })
          .map<IssueRow>((listing) => {
            const connections = listing
              .connections({
                order_by: [{ connection: { app: { name: 'asc' } } }],
              })
              .map((connection) => ({
                id: connection.id,
                name: connection.name,
                iconRound: connection.connection.app.iconRound,
              }));

            const listingName = getListingName(listing);

            return {
              id: listing.id,
              type: 'listing-owner-missing',
              table: 'listing',
              level: 'warning',
              message: 'No ownership assigned',
              itemName: listingName,
              date: listing.createdAt!,
              item: (
                <IssueListingNameCell
                  address={getListingAddress(listing).full}
                  connections={connections}
                  name={listingName}
                />
              ),
            };
          });

  const whereMissingClass = {
    ...whereListings({
      tenantId: args.teamId,
      dashboard: 'propertyManager',
      search: args.search,
      status: 'active',
      ownerPortalContactId: null,
      GL: false,
    }),
    _not: {
      settingsRight: args.listingClassMapping
        ? {
            key: { _eq: args.listingClassMapping.mapping.key },
            _or: whereSettingAcrossAutomations({
              leftConnectionId:
                args.listingClassMapping.automation.finalyticConnectionId,
              leftSchema: args.listingClassMapping.mapping.leftType,
              rightSchema: args.listingClassMapping.mapping.rightType,
              rightConnectionId:
                args.listingClassMapping.automation.accountingConnectionId ||
                '',
              automationId:
                args.listingClassMapping.automation.automation?.automationId,
            }),
          }
        : {},
    },
  };

  const disableListingClassMapping =
    !args.listingClassMapping ||
    args.GL ||
    args.level === 'error' ||
    !args.listingClassMapping.automation.automation ||
    !args.listingClassMapping.accounting;

  const missingClassAggregate = disableListingClassMapping
    ? 0
    : q
        .listingAggregate({
          where: whereMissingClass,
        })
        .aggregate?.count() || 0;

  const missingClasses =
    args.onlyAggregate || disableListingClassMapping
      ? []
      : q
          .listings({
            where: whereMissingClass,
            limit,
            offset,
            order_by: [
              {
                createdAt: 'desc',
              },
            ],
          })
          .map<IssueRow>((listing) => {
            const connections = listing
              .connections({
                order_by: [{ connection: { app: { name: 'asc' } } }],
              })
              .map((connection) => ({
                id: connection.id,
                name: connection.name,
                iconRound: connection.connection.app.iconRound,
              }));

            const listingName = getListingName(listing);

            return {
              id: listing.id,
              type: 'listing-class-missing',
              table: 'listing',
              level: 'warning',
              message: 'No class assigned',
              itemName: listingName,
              date: listing.createdAt!,
              item: (
                <IssueListingNameCell
                  address={getListingAddress(listing).full}
                  connections={connections}
                  name={listingName}
                />
              ),
            };
          });

  const aggregate =
    failedConnectionAggregate +
    missingOwnerAggregate +
    missingClassAggregate +
    outdatedConnections.length +
    duplicatedPayments.length;

  const list = sortBy(
    [
      ...outdatedConnections,
      ...failedConnections,
      ...duplicatedPayments,
      ...missingOwners,
      ...missingClasses,
    ],
    'date',
    'desc'
  );

  return {
    aggregate,
    list,
  };
}

type IssueConnectionNameCellProps = {
  icon: Maybe<string>;
  appId?: Maybe<string>;
  name: Maybe<string>;
  uniqueRef?: Maybe<string>;
};

const IssueAppIconById = ({
  appId,
  size = 24,
}: {
  appId: string;
  size?: number;
}) => {
  const queryData = useApiQuery(
    async (api, signal, args) => {
      const result = await api.GET('/apps/{id}', {
        params: {
          path: {
            id: args.appId,
          },
        },
        signal,
      });

      if (result.error) throw result.error;

      return {
        app: result.data,
      };
    },
    {
      skip: !appId,
      queryGroup: 'apps',
      uniqueQueryKey: `app-${appId}`,
      variables: {
        appId,
      },
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  if (queryData.isLoading) {
    return <Skeleton height={size} width={size} />;
  }

  return (
    <Avatar
      src={queryData.data?.app.icon}
      size={size}
      styles={{
        placeholder: { visibility: 'hidden' },
      }}
      sx={(theme) => ({
        border: `1px solid ${theme.colors.gray[2]}`,
      })}
    />
  );
};

const IssueConnectionNameCell = ({
  icon,
  name,
  uniqueRef,
  appId,
}: IssueConnectionNameCellProps) => {
  return (
    <Group wrap="nowrap" gap="sm">
      {!icon && appId ? (
        <IssueAppIconById appId={appId} />
      ) : (
        <Avatar
          src={icon}
          size="sm"
          styles={{
            placeholder: { visibility: 'hidden' },
          }}
          sx={(theme) => ({
            border: `1px solid ${theme.colors.gray[2]}`,
          })}
        />
      )}
      <Box>
        <Text size="sm" component="p" fw={500} m={0}>
          {name}
        </Text>
        {uniqueRef && (
          <Text size="xs" component="p" c="gray">
            {uniqueRef}
          </Text>
        )}
      </Box>
    </Group>
  );
};

type IssueListingConnection = {
  id: Maybe<string>;
  iconRound: Maybe<string>;
  name: Maybe<string>;
};

const IssueConnectionIcons = ({
  connections: value,
}: {
  connections: Maybe<IssueListingConnection[]>;
}) => {
  const max = 2;
  const connections = (value || []).filter((connection) => connection.id);
  const slicedConnections = connections.slice(0, max);
  const restConnections = connections.slice(max);

  return (
    <Avatar.Group sx={{ background: 'transparent' }}>
      {slicedConnections.map((connection) => (
        <Tooltip
          label={connection?.name}
          key={connection?.id}
          withinPortal
          withArrow
          position="top"
        >
          <Avatar
            src={connection?.iconRound}
            size="sm"
            sx={(theme) => ({
              border: `1px solid ${theme.colors.gray[2]}`,
            })}
          />
        </Tooltip>
      ))}
      {restConnections.length > 0 && (
        <Tooltip
          withArrow
          withinPortal
          radius="sm"
          label={restConnections.map((connection) => (
            <Group gap="xs" key={connection.id} wrap="nowrap" px="xs">
              <Avatar
                src={connection?.iconRound}
                size="xs"
                sx={(theme) => ({
                  border: `1px solid ${theme.colors.gray[2]}`,
                })}
              />
              <Text>{connection.name}</Text>
            </Group>
          ))}
        >
          <Avatar radius="xl" size="sm">
            +{connections.length - max}
          </Avatar>
        </Tooltip>
      )}
    </Avatar.Group>
  );
};

const IssueListingNameCell = ({
  name,
  address,
  connections,
}: {
  name: Maybe<string>;
  address: Maybe<string>;
  connections: Maybe<IssueListingConnection[]>;
}) => {
  return (
    <Group wrap="nowrap">
      {!!connections?.length && (
        <Box miw={25}>
          <IssueConnectionIcons connections={connections} />
        </Box>
      )}
      <Box>
        <Text lineClamp={1} size="sm" sx={{ display: 'block' }}>
          {name}
        </Text>
        {address && (
          <Text c="gray" size="xs" lineClamp={1} sx={{ display: 'block' }}>
            {address}
          </Text>
        )}
      </Box>
    </Group>
  );
};
